import React, { useEffect, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius } from "../theme";
import { getAuthHeader } from "../session";

const money = (n) => `$${Number(n).toFixed(2)}`;
const FRONT = ["owner", "manager", "waiter"];
const PAID_VIA = [["phone_card", "Card (by phone)"], ["cash", "Cash"], ["other", "Other"]];

// The Pending payment / Upcoming / Kitchen / Ready tabs for online orders (shown on the Kitchen and Waiter screens).
export default function OnlineOrdersPanel({ role, readOnly, data, error, reload }) {
  const front = FRONT.includes(role);
  const tabs = front ? ["pending", "upcoming", "kitchen", "ready"] : ["kitchen", "ready"];
  const LABEL = { pending: "Pending payment", upcoming: "Upcoming", kitchen: "Kitchen", ready: "Ready for pickup" };
  const [tab, setTab] = useState(null);
  const [payFor, setPayFor] = useState(null); // {id, action}
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (tab === null && data) setTab(front && data.counts.pending > 0 ? "pending" : "kitchen");
  }, [data, tab, front]);

  const act = async (order, action, extra = {}) => {
    setMsg("");
    try {
      const res = await axios.post(endpoints.onlineOrderAction, { id: order.id, action, ...extra }, { headers: getAuthHeader() });
      setPayFor(null);
      if (res.data.manual_refund_needed) window.alert("This order was paid by phone or cash. Remember to refund the customer yourself.");
      reload();
    } catch (err) {
      setMsg(err.response?.data?.error || "That didn't work. Please try again.");
      reload();
    }
  };
  const cancel = (o) => {
    const refund = o.payment_method === "stripe" && o.payment_status === "paid";
    const reason = window.prompt(refund ? `Cancel order ${o.order_number}? ${money(o.total)} will be refunded to the customer's card.\n\nReason (optional):` : `Cancel order ${o.order_number}?\n\nReason (optional):`, "");
    if (reason === null) return;
    act(o, "cancel", { reason });
  };

  if (!data) return <p style={styles.muted}>Loading online orders…</p>;
  const list = data[tab] || [];

  const buttons = (o) => {
    const b = [];
    const dis = readOnly;
    if (tab === "pending") {
      b.push(<button key="pay" style={styles.primary} disabled={dis} onClick={() => setPayFor({ id: o.id, action: "confirm_payment" })}>Confirm payment</button>);
      if (!o.over_limit) b.push(<button key="pp" style={styles.btn} disabled={dis} onClick={() => act(o, "send_to_kitchen")}>Send to kitchen, pay at pickup</button>);
    }
    if (tab === "upcoming") b.push(<button key="now" style={styles.primary} disabled={dis} onClick={() => act(o, "start_now")}>Start now</button>);
    if (tab === "kitchen") {
      if (o.status === "new") b.push(<button key="prep" style={styles.primary} disabled={dis} onClick={() => act(o, "prepare")}>Start preparing</button>);
      b.push(<button key="ready" style={o.status === "new" ? styles.btn : styles.primary} disabled={dis} onClick={() => act(o, "ready")}>Mark ready</button>);
    }
    if (tab === "ready" && front) {
      b.push(<button key="pick" style={styles.primary} disabled={dis} onClick={() => (o.payment_status === "pay_at_pickup" ? setPayFor({ id: o.id, action: "picked_up" }) : act(o, "picked_up"))}>Picked up</button>);
      b.push(<button key="ns" style={styles.btn} disabled={dis} onClick={() => act(o, "no_show")}>No-show</button>);
    }
    if (front && tab !== "kitchen") b.push(<button key="cx" style={styles.danger} disabled={dis} onClick={() => cancel(o)}>Cancel</button>);
    if (front && tab === "kitchen") b.push(<button key="cx" style={styles.danger} disabled={dis} onClick={() => cancel(o)}>Cancel</button>);
    return b;
  };

  return (
    <div style={styles.panel} data-testid="online-panel">
      <div style={styles.tabs} role="tablist">
        {tabs.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} style={{ ...styles.tab, ...(tab === t ? styles.tabOn : {}) }}>
            {LABEL[t]} {data.counts[t] > 0 && <span style={{ ...styles.badge, ...(t === "pending" ? styles.badgeHot : {}) }} data-testid={`badge-${t}`}>{data.counts[t]}</span>}
          </button>
        ))}
      </div>
      {(msg || error) && <div style={styles.err} role="alert">{msg || error}</div>}
      {tab === "pending" && <p style={styles.hint}>Call the customer, then confirm payment. The order goes to the kitchen when you do.</p>}
      {list.length === 0 && <p style={styles.muted}>{tab === "pending" ? "Nothing waiting for payment." : tab === "upcoming" ? "No scheduled orders." : tab === "kitchen" ? "No online orders in the kitchen." : "Nothing ready for pickup."}</p>}
      {list.map((o) => (
        <div key={o.id} style={styles.card} data-testid="online-order" data-status={o.status}>
          <div style={styles.top}>
            <strong style={styles.num}>{o.order_number}</strong>
            <span style={styles.pickup}>{o.pickup_type === "scheduled" ? `Pickup ${o.pickup_text}` : o.pickup_text}</span>
          </div>
          <div style={styles.who}>
            {front ? (<>{o.customer_name} · <a href={`tel:${o.phone}`} style={styles.tel}>{o.phone_display}</a></>) : o.customer_name}
          </div>
          <ul style={styles.items}>
            {o.items.map((i, k) => <li key={k}><strong>{i.qty}×</strong> {i.name}{i.notes ? <em> ({i.notes})</em> : null}</li>)}
          </ul>
          {o.notes && <div style={styles.note}>Note: {o.notes}</div>}
          <div style={styles.badges}>
            <span style={styles.total}>{money(o.total)}</span>
            {o.payment_status === "paid" && <span style={{ ...styles.tag, ...styles.tagOk }}>{o.payment_method === "stripe" ? "Paid online" : `Paid (${(o.paid_via || "").replace("_", " ")})`}</span>}
            {o.payment_status === "pay_at_pickup" && <span style={{ ...styles.tag, ...styles.tagWarn }}>Pay at pickup</span>}
            {o.over_limit && o.status === "awaiting_payment" && <span style={{ ...styles.tag, ...styles.tagBad }}>Over limit, payment required</span>}
            {o.status === "awaiting_payment" && o.hold_seconds_left !== null && <span style={styles.tag}>Expires in {Math.ceil(o.hold_seconds_left / 60)} min</span>}
          </div>
          {payFor && payFor.id === o.id ? (
            <div style={styles.payRow}>
              <span style={{ fontSize: 13 }}>{payFor.action === "confirm_payment" ? "How did they pay?" : "How did they pay at pickup?"}</span>
              {PAID_VIA.map(([v, label]) => <button key={v} style={styles.primary} onClick={() => act(o, payFor.action, { paid_via: v })}>{label}</button>)}
              <button style={styles.btn} onClick={() => setPayFor(null)}>Back</button>
            </div>
          ) : (
            <div style={styles.actions}>{buttons(o)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

const styles = {
  panel: { maxWidth: 900, margin: "0 auto", padding: space[3], fontFamily: fonts.body, color: colors.textPrimary },
  tabs: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: space[3] },
  tab: { padding: "10px 14px", borderRadius: radius.md, border: `1px solid ${colors.border}`, background: colors.surface, cursor: "pointer", fontSize: 14 },
  tabOn: { background: colors.brandBlue, color: "#fff", borderColor: colors.brandBlue, fontWeight: 600 },
  badge: { background: "#e5e7eb", color: "#111827", borderRadius: 999, padding: "1px 8px", fontSize: 12, marginLeft: 4 },
  badgeHot: { background: "#dc2626", color: "#fff" },
  hint: { fontSize: 13, color: colors.textSecondary, margin: `0 0 ${space[3]}` },
  muted: { color: colors.textMuted, fontSize: 14 },
  err: { background: "#fef3f2", color: "#b42318", borderRadius: radius.md, padding: space[3], marginBottom: space[3], fontSize: 14 },
  card: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: space[3], marginBottom: space[3] },
  top: { display: "flex", justifyContent: "space-between", gap: space[2], flexWrap: "wrap", alignItems: "baseline" },
  num: { fontFamily: fonts.display, fontSize: 20, letterSpacing: "0.04em" },
  pickup: { fontSize: 13, color: colors.textSecondary },
  who: { fontSize: 14, margin: `${space[1]} 0` },
  tel: { color: colors.brandBlue, fontWeight: 600, textDecoration: "none" },
  items: { margin: `${space[2]} 0`, paddingLeft: 18, fontSize: 15 },
  note: { fontSize: 13, background: "#fffaeb", color: "#93370d", borderRadius: radius.sm, padding: "6px 8px", margin: `${space[2]} 0` },
  badges: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", margin: `${space[2]} 0` },
  total: { fontWeight: 700, marginRight: 6 },
  tag: { fontSize: 12, borderRadius: 999, padding: "2px 10px", background: colors.surfaceMuted, color: colors.textSecondary },
  tagOk: { background: "#dcfce7", color: "#166534" },
  tagWarn: { background: "#fef3c7", color: "#92400e" },
  tagBad: { background: "#fee2e2", color: "#b91c1c", fontWeight: 600 },
  actions: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: space[2] },
  payRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: space[2] },
  btn: { padding: "12px 16px", borderRadius: radius.md, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer", fontSize: 14, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" },
  primary: { padding: "12px 16px", borderRadius: radius.md, border: "none", background: colors.brandBlue, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" },
  danger: { padding: "12px 16px", borderRadius: radius.md, border: "1px solid #fca5a5", background: "#fff", color: "#b42318", cursor: "pointer", fontSize: 14, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" },
};
